import { ChangeDetectionStrategy, Component, CUSTOM_ELEMENTS_SCHEMA, EventEmitter, Input, Output } from '@angular/core';
import { CommonModule } from '@angular/common';

import { GableDismissReason } from '../../../../services/gable/gable.service';

/**
 * Result returned by the dismiss-reason dialog: the chosen reason enum, or
 * `null` when the user backs out (Cancel / close / backdrop). A `null` result
 * ABORTS the dismissal — the proposal stays in the queue.
 */
export type DismissReasonDialogResult = GableDismissReason | null;

/** One reason choice — enum `value` paired with its human-readable `label`. */
interface DismissReasonChoice {
    value: GableDismissReason;
    label: string;
}

/**
 * Dialog that asks WHY a Gable suggestion is being dismissed before
 * rejecting it. Mirrors {@link ApprovalDialogComponent}'s shell.
 *
 * The four reasons map to the {@link GableDismissReason} enum the reject
 * endpoint stores so the Activity feed can later show WHY each item was
 * dismissed. Picking a reason emits `closed` with that enum; Cancel emits null.
 */
@Component({
    selector: 'app-gable-dismiss-reason-dialog',
    templateUrl: './dismiss-reason-dialog.component.html',
    styleUrls: ['./dismiss-reason-dialog.component.scss'],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush,
    schemas: [CUSTOM_ELEMENTS_SCHEMA],
    imports: [CommonModule],
})
export class DismissReasonDialogComponent {
    @Input() open = false;
    @Output() readonly closed = new EventEmitter<DismissReasonDialogResult>();

    /** The four dismiss reasons, in display order. Copy per contract §3. */
    readonly reasons: DismissReasonChoice[] = [
        { value: 'not_needed', label: 'Not needed right now' },
        { value: 'manual', label: 'I\'ll handle this manually' },
        { value: 'wrong', label: 'Gable got this wrong' },
        { value: 'policy', label: 'Against our policy' },
    ];

    /** `ph-action-list` items — same four reasons, kit's flat shape. */
    readonly reasonItems = this.reasons.map(({ value, label }) => ({ value, label }));

    onPick(reason: string): void {
        this.closed.emit(reason as GableDismissReason);
    }

    onCancel(): void {
        this.closed.emit(null);
    }
}
